"use client"

import * as React from "react"
import { format } from "date-fns"
import { toZonedTime } from "date-fns-tz"
import { CheckIcon, ChevronDownIcon, XIcon } from "lucide-react"
import { type DateRange } from "react-day-picker"

import {
  Button,
  Calendar,
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  InputGroup,
  InputGroupAddon,
  InputGroupInput,
  InputGroupText,
  Label,
  PeriodPreset,
  Popover,
  PopoverContent,
  PopoverTrigger,
  cn,
  resolvePeriodRange,
} from "@ibada/ui"
import { dateOnlyToDatePickerValue } from "@ibada/shared-types"

import { AliveAvatar } from "@/shared/components/alive-cells"
import { useLookup } from "@/shared/hooks/use-lookup"
import { LookupType } from "@/shared/model/lookup-types"
import { TenantScopeContext } from "@/shared/model/tenant-scope-context"
import { useTranslations } from "@/i18n"

/** One active facet, shown as a removable green chip. */
export type ExplorerFilterChip = {
  id: string
  label: string
  /** Avatar beside the label (who made it). */
  avatar?: { name: string; src: string | null }
  onRemove: () => void
}

/** The facets the pill bar edits. Every other facet stays reachable as a chip only. */
export enum ExplorerPillFacet {
  Actor = "actor",
  Type = "type",
  Account = "account",
  Amount = "amount",
  Period = "period",
}

const PILL_CLASS =
  "h-8 gap-1 rounded-full border-border/80 bg-card px-3 text-[13px] font-bold text-muted-foreground shadow-none hover:text-foreground"

const PERIOD_PRESETS: readonly PeriodPreset[] = [
  PeriodPreset.ThisMonth,
  PeriodPreset.LastMonth,
  PeriodPreset.ThisQuarter,
  PeriodPreset.YearToDate,
  PeriodPreset.LastYear,
]

/** The removable green chip of an active filter. */
export function ExplorerActiveChip({ chip, removeLabel }: { chip: ExplorerFilterChip; removeLabel: string }) {
  return (
    <span
      className={cn(
        "inline-flex h-8 max-w-[22rem] items-center gap-1.5 rounded-full border border-primary/30 bg-primary/10 pr-1 text-[13px] font-bold text-foreground",
        chip.avatar ? "pl-1" : "pl-3",
      )}
    >
      {chip.avatar ? <AliveAvatar small name={chip.avatar.name} src={chip.avatar.src} /> : null}
      <span className='truncate'>{chip.label}</span>
      <button
        type='button'
        aria-label={removeLabel}
        onClick={chip.onRemove}
        className='inline-flex size-6 shrink-0 items-center justify-center rounded-full text-primary hover:bg-primary/15 focus-visible:outline-2 focus-visible:outline-primary'
      >
        <XIcon className='size-3.5' strokeWidth={2.5} aria-hidden />
      </button>
    </span>
  )
}

function PillTrigger({ label, ...props }: { label: string } & React.ComponentProps<typeof Button>) {
  return (
    <Button type='button' variant='outline' size='sm' className={PILL_CLASS} {...props}>
      {label}
      <ChevronDownIcon className='size-3.5 opacity-70' aria-hidden />
    </Button>
  )
}

/** A server-searched pick (who, which account) in a popover list. */
function LookupPill({
  label,
  lookupType,
  workspaceId,
  branchId,
  parishContext,
  searchPlaceholder,
  emptyLabel,
  onPick,
}: {
  label: string
  lookupType: LookupType
  workspaceId?: string
  branchId?: string
  parishContext: boolean
  searchPlaceholder: string
  emptyLabel: string
  onPick: (pick: { id: string; label: string }) => void
}) {
  const [open, setOpen] = React.useState(false)
  const [query, setQuery] = React.useState("")
  const lookup = useLookup({
    type: lookupType,
    query,
    limit: 15,
    workspaceId,
    branchId: parishContext ? undefined : branchId,
    context: parishContext ? TenantScopeContext.Parish : TenantScopeContext.Branch,
    enabled: open && Boolean(workspaceId),
  })
  const items = lookup.data?.items ?? []

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <PillTrigger label={label} />
      </PopoverTrigger>
      <PopoverContent align='start' className='w-72 rounded-2xl p-0'>
        <Command shouldFilter={false}>
          <CommandInput value={query} onValueChange={setQuery} placeholder={searchPlaceholder} />
          <CommandList>
            {!lookup.isFetching ? <CommandEmpty>{emptyLabel}</CommandEmpty> : null}
            <CommandGroup>
              {items.map((item) => (
                <CommandItem
                  key={item.id}
                  value={item.id}
                  onSelect={() => {
                    onPick({ id: item.id, label: item.label })
                    setOpen(false)
                    setQuery("")
                  }}
                >
                  <span className='truncate'>{item.label}</span>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  )
}

/** A fixed list of choices (the event type). */
function ChoicePill({
  label,
  options,
  value,
  onPick,
}: {
  label: string
  options: ReadonlyArray<{ value: string; label: string }>
  value?: string
  onPick: (value: string) => void
}) {
  const [open, setOpen] = React.useState(false)
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <PillTrigger label={label} />
      </PopoverTrigger>
      <PopoverContent align='start' className='w-56 rounded-2xl p-0'>
        <Command>
          <CommandList>
            <CommandGroup>
              {options.map((option) => (
                <CommandItem
                  key={option.value}
                  value={option.value}
                  onSelect={() => {
                    onPick(option.value)
                    setOpen(false)
                  }}
                  className='justify-between'
                >
                  <span>{option.label}</span>
                  {option.value === value ? <CheckIcon className='size-4 text-primary' aria-hidden /> : null}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  )
}

/** A minimum / maximum amount, with the currency beside each field. */
function AmountPill({
  label,
  currencyPrefix,
  from,
  to,
  onApply,
}: {
  label: string
  currencyPrefix: string
  from?: number
  to?: number
  onApply: (range: { from?: number; to?: number }) => void
}) {
  const t = useTranslations("finance.audit.explorer.filters")
  const [open, setOpen] = React.useState(false)
  const [min, setMin] = React.useState("")
  const [max, setMax] = React.useState("")
  React.useEffect(() => {
    if (!open) return
    setMin(from !== undefined ? String(from) : "")
    setMax(to !== undefined ? String(to) : "")
  }, [open, from, to])

  const parse = (text: string): number | undefined => {
    const normalized = text.replace(/\s/gu, "").replace(",", ".")
    if (!normalized) return undefined
    const value = Number(normalized)
    return Number.isFinite(value) && value >= 0 ? value : undefined
  }
  const minValue = parse(min)
  const maxValue = parse(max)
  const inverted = minValue !== undefined && maxValue !== undefined && minValue > maxValue

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <PillTrigger label={label} />
      </PopoverTrigger>
      <PopoverContent align='start' className='w-72 rounded-2xl'>
        <form
          className='flex flex-col gap-3'
          onSubmit={(event) => {
            event.preventDefault()
            if (inverted) return
            onApply({ from: minValue, to: maxValue })
            setOpen(false)
          }}
        >
          <div className='grid grid-cols-2 gap-2'>
            <div className='flex flex-col gap-1.5'>
              <Label htmlFor='explorer-amount-min' className='text-xs text-muted-foreground'>
                {t("minimum")}
              </Label>
              <InputGroup>
                <InputGroupAddon align='inline-start'>
                  <InputGroupText>{currencyPrefix}</InputGroupText>
                </InputGroupAddon>
                <InputGroupInput id='explorer-amount-min' inputMode='decimal' value={min} onChange={(e) => setMin(e.target.value)} />
              </InputGroup>
            </div>
            <div className='flex flex-col gap-1.5'>
              <Label htmlFor='explorer-amount-max' className='text-xs text-muted-foreground'>
                {t("maximum")}
              </Label>
              <InputGroup>
                <InputGroupAddon align='inline-start'>
                  <InputGroupText>{currencyPrefix}</InputGroupText>
                </InputGroupAddon>
                <InputGroupInput id='explorer-amount-max' inputMode='decimal' value={max} onChange={(e) => setMax(e.target.value)} />
              </InputGroup>
            </div>
          </div>
          {inverted ? <p className='text-xs text-destructive'>{t("amountInverted")}</p> : null}
          <div className='flex justify-end'>
            <Button type='submit' size='sm' className='rounded-full' disabled={inverted || (minValue === undefined && maxValue === undefined)}>
              {t("apply")}
            </Button>
          </div>
        </form>
      </PopoverContent>
    </Popover>
  )
}

/** A period: the usual presets, resolved in the workspace's zone, or any range on the calendar. */
function PeriodPill({
  label,
  timeZone,
  dateFrom,
  dateTo,
  onApply,
}: {
  label: string
  timeZone: string
  dateFrom?: string
  dateTo?: string
  onApply: (range: { from?: string; to?: string }) => void
}) {
  const t = useTranslations("finance.audit.explorer.filters")
  const tPeriod = useTranslations("finance.period")
  const [open, setOpen] = React.useState(false)
  const [range, setRange] = React.useState<DateRange | undefined>()
  React.useEffect(() => {
    if (!open) return
    setRange({
      from: dateFrom ? (dateOnlyToDatePickerValue(dateFrom) ?? undefined) : undefined,
      to: dateTo ? (dateOnlyToDatePickerValue(dateTo) ?? undefined) : undefined,
    })
  }, [open, dateFrom, dateTo])

  const presets = React.useMemo(() => {
    const now = toZonedTime(new Date(), timeZone)
    return PERIOD_PRESETS.flatMap((preset) => {
      const resolved = resolvePeriodRange(preset, { now, weekStartsOn: 1 })
      return resolved
        ? [{ preset, from: format(resolved.start, "yyyy-MM-dd"), to: format(resolved.end, "yyyy-MM-dd") }]
        : []
    })
  }, [timeZone])

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <PillTrigger label={label} />
      </PopoverTrigger>
      <PopoverContent align='start' className='w-auto rounded-2xl p-3'>
        <div className='flex gap-3'>
          <ul className='flex w-40 flex-col gap-0.5'>
            {presets.map((item) => (
              <li key={item.preset}>
                <button
                  type='button'
                  onClick={() => {
                    onApply({ from: item.from, to: item.to })
                    setOpen(false)
                  }}
                  className='w-full rounded-full px-3 py-1.5 text-left text-[13px] font-semibold hover:bg-muted'
                >
                  {tPeriod(`presets.${item.preset}` as never)}
                </button>
              </li>
            ))}
          </ul>
          <div className='flex flex-col gap-2 border-l border-border/60 pl-3'>
            <span className='px-1 text-xs font-bold text-muted-foreground'>{tPeriod("custom")}</span>
            <Calendar mode='range' selected={range} onSelect={setRange} numberOfMonths={1} />
            <div className='flex justify-end'>
              <Button
                type='button'
                size='sm'
                className='rounded-full'
                disabled={!range?.from}
                onClick={() => {
                  if (!range?.from) return
                  onApply({ from: format(range.from, "yyyy-MM-dd"), to: format(range.to ?? range.from, "yyyy-MM-dd") })
                  setOpen(false)
                }}
              >
                {t("apply")}
              </Button>
            </div>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  )
}

export type ExplorerFilterBarProps = {
  chips: ExplorerFilterChip[]
  /** Which pill facets are already active (and so shown as chips instead of pills). */
  activeFacets: ReadonlySet<ExplorerPillFacet>
  typeOptions: ReadonlyArray<{ value: string; label: string }>
  typeValue?: string
  amountFrom?: number
  amountTo?: number
  dateFrom?: string
  dateTo?: string
  currencyPrefix: string
  timeZone: string
  workspaceId?: string
  branchId?: string
  parishContext: boolean
  onPickActor: (pick: { id: string; label: string }) => void
  onPickAccount: (pick: { id: string; label: string }) => void
  onPickType: (value: string) => void
  onApplyAmount: (range: { from?: number; to?: number }) => void
  onApplyPeriod: (range: { from?: string; to?: string }) => void
  onClearAll: () => void
}

/**
 * The Explorer's filters (I1): the active ones as removable green chips, then a pill for each of who, type,
 * account, amount and period not yet set, then "Tout effacer". Every change goes through the page URL.
 */
export function ExplorerFilterBar(props: ExplorerFilterBarProps) {
  const t = useTranslations("finance.audit.explorer.filters")
  const tExplorer = useTranslations("finance.audit.explorer")
  const { activeFacets } = props

  return (
    <div className='flex flex-wrap items-center gap-1.5'>
      {props.chips.map((chip) => (
        <ExplorerActiveChip key={chip.id} chip={chip} removeLabel={t("remove", { label: chip.label })} />
      ))}
      {activeFacets.has(ExplorerPillFacet.Actor) ? null : (
        <LookupPill
          label={t("who")}
          lookupType={LookupType.Users}
          workspaceId={props.workspaceId}
          branchId={props.branchId}
          parishContext={props.parishContext}
          searchPlaceholder={t("search")}
          emptyLabel={t("noResults")}
          onPick={props.onPickActor}
        />
      )}
      {activeFacets.has(ExplorerPillFacet.Type) ? null : (
        <ChoicePill label={t("type")} options={props.typeOptions} value={props.typeValue} onPick={props.onPickType} />
      )}
      {activeFacets.has(ExplorerPillFacet.Account) ? null : (
        <LookupPill
          label={t("account")}
          lookupType={LookupType.AccountingAccounts}
          workspaceId={props.workspaceId}
          branchId={props.branchId}
          parishContext={props.parishContext}
          searchPlaceholder={t("search")}
          emptyLabel={t("noResults")}
          onPick={props.onPickAccount}
        />
      )}
      {activeFacets.has(ExplorerPillFacet.Amount) ? null : (
        <AmountPill
          label={t("amount")}
          currencyPrefix={props.currencyPrefix}
          from={props.amountFrom}
          to={props.amountTo}
          onApply={props.onApplyAmount}
        />
      )}
      {activeFacets.has(ExplorerPillFacet.Period) ? null : (
        <PeriodPill
          label={t("periodAll")}
          timeZone={props.timeZone}
          dateFrom={props.dateFrom}
          dateTo={props.dateTo}
          onApply={props.onApplyPeriod}
        />
      )}
      {props.chips.length > 0 ? (
        <Button type='button' variant='text' size='sm' className='h-8 rounded-full px-2 text-[13px] font-bold text-muted-foreground hover:text-foreground' onClick={props.onClearAll}>
          {tExplorer("clearAll")}
        </Button>
      ) : null}
    </div>
  )
}
